<script lang="ts" setup>
import { computed, ref } from 'vue';
import DashboardsService, { GridItemAttributes, WidgetModel, WidgetConfigModel } from '@/modules/core/dashboards/services/DashboardsService';
import ActiveSubstitutions from '@/modules/core/dashboards/components/widgets/active-substitutions/Widget.vue';
import AiUsage from '@/modules/core/dashboards/components/widgets/ai-usage/Widget.vue';

const emit = defineEmits<{
    (e: 'edit-widget', value: WidgetConfigModel): void;
    (e: 'delete-widget', id: string): void;
}>();

const props = defineProps<{
    config: WidgetConfigModel;
    isDesignMode: boolean;
}>();

const widget = ref<WidgetModel>(null);

const gridStackAttributes = computed<GridItemAttributes>(() => ({
    id: `x${props.config.id}`,
    'gs-id': `x${props.config.id}`,
    'gs-x': props.config.x,
    'gs-y': props.config.y,
    'gs-w': props.config.width,
    'gs-h': props.config.height,
    'gs-no-resize': true,
}));

const component = computed(() =>
{
    switch (widget.value.type)
    {
        case 'table':
            return  ActiveSubstitutions;
        case 'chart':
            return AiUsage;

        default: return null;
    }
});

const changeSettings = () => emit('edit-widget', props.config);
const deleteWidget = (id: string) => emit('delete-widget', id);

const fetchData = async () =>
{
    const response = await DashboardsService.getWidget(props.config.id);

    widget.value = response;
};

const init = () =>
{
    fetchData();
};

init();
</script>

<template>
    <div class="grid-stack-item" v-bind="gridStackAttributes">
        <div class="grid-stack-item-content" :class="{ move: isDesignMode }">
            <div v-if="widget" class="widget">
                <div class="header">
                    <h4>{{ widget.name }}</h4>

                    <IdeoDropdown v-if="isDesignMode" variant="none" dropleft no-caret class="options">
                        <template #button-content>
                            <i class="icon far fa-ellipsis-v"></i>
                        </template>
                        <template #default>
                            <IdeoDropdownItemButton @click="changeSettings">
                                <i class="icon far fa-gear"></i>
                                <span>{{ $t('[[[Ustawienia]]]') }}</span>
                            </IdeoDropdownItemButton>
                            <IdeoDropdownItemButton
                                :message="$t('[[[Czy na pewno chcesz usunąć widget?]]]')"
                                :value="widget.id"
                                message-placement="left"
                                @confirm="deleteWidget"
                            >
                                <i class="icon far fa-trash-alt"></i> {{ $t('[[[Usuń]]]') }}
                            </IdeoDropdownItemButton>
                        </template>
                    </IdeoDropdown>
                </div>
                <div class="content scroll">
                    <template v-if="component">
                        <component :is="component" :widget="widget" :config="config" :properties-mode="false"></component>
                    </template>
                    <span v-else>{{ $t('[[[Nie znaleziono pasującego widgetu]]]') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.grid-stack-item-content {
    display: flex;
    flex-direction: column;
    position: relative;

    .widget {
        background: var(--bs-body-bg);
        padding: 25px 15px;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: 5px;
        flex-grow: 1;
        display: flex;
        overflow: hidden;
        flex-direction: column;

        &.smaller-padding {
            padding: 10px;
        }

        .header {
            margin-bottom: 15px;
            justify-content: space-between;
            display: flex;
            gap: 10px;
            align-items: center;

            &.smaller-margin {
                margin-bottom: 10px;
            }

            h4 {
                line-height: 1.5rem;
                font-size: 1.25rem;
                font-weight: 700;
                margin: 0;

                &.smaller-header {
                    line-height: 1.5rem;
                    font-size: 1rem;
                }
            }
        }

        .icon {
            font-size: 1rem;
        }

        .options {
            margin: -6px -12px;

            :deep(button) {
                margin: 0;

                &:active {
                    border-color: transparent;
                }
            }
        }

        .content {
            flex: 1 1 100%;
            display: flex;
            flex-direction: column;
        }
    }

    &.move {
        cursor: move;
    }

    :deep(.link-more) {
        display: inline-block;
        margin-top: 15px;
        font-size: 1rem;
        line-height: 1.75rem;
        color: var(--bs-primary);
    }
}
</style>
